import React from 'react';
import _ from 'lodash';

import {
    Box,
    Button,
    Chip,
    Typography
} from '@mui/material';
import {
    Cancel,
    HourglassTop,
    Language,
    Mail,
    OpenInNew,
    Phone,
    Place,
    ThumbUp,
    Verified
} from '@mui/icons-material';

import { formatPhone, formatExternalUrl, formatNumber } from '@utils/parsing';
import { colors, FONT_FAMILY_DISPLAY, icons } from '@/theme';
import { TenantProfileType } from '@types';
import {
    carrierNameCapitalized,
    nonCarrierNameCapitalized,
    websiteProfileUrlBase
} from '@env';
import { getFairtransportValueLevel, isTenantFairtransportMember } from '@utils/metadata';
import Logotype from '../TenantProfileComponents/Logotype';

export type ProfileHeroProps = {
    activeSubscription: TenantProfileType['activeSubscription'];
    allabolagUrl: TenantProfileType['allabolagUrl'];
    city: TenantProfileType['city'];
    email: string | undefined;
    isAccepted: TenantProfileType['isAccepted'];
    isCarrier: TenantProfileType['isCarrier'];
    isPending: TenantProfileType['isPending'];
    isRejected: TenantProfileType['isRejected'];
    isPublic: TenantProfileType['isPublic'];
    fairtransportMembership: TenantProfileType['fairtransportMembership'];
    fairtransportUrl: TenantProfileType['fairtransportUrl'];
    logoUrl: TenantProfileType['logoUrl'];
    name: TenantProfileType['name'];
    orgNumber: TenantProfileType['orgNumber'];
    phone: string | undefined;
    street: TenantProfileType['street'];
    website: TenantProfileType['website'];
    slug: TenantProfileType['slug'];
    zip: TenantProfileType['zip'];
    revenue?: TenantProfileType['revenue'];
    numberOfTrucks?: TenantProfileType['numberOfTrucks'];
    recommendedByCurrentTenant?: boolean;
    canOpenMap?: boolean;
    onOpenMap?: () => void;
};

const labelSx = {
    letterSpacing: '0.2em',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase' as const
};

const onNavyChipSx = {
    height: 36,
    px: 1,
    color: colors.white,
    bgcolor: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.15)',
    fontWeight: 500,
    '& .MuiChip-icon': { color: 'inherit' }
};

const ContactIcon: Record<string, React.JSX.Element> = {
    website: <Language sx={{ fontSize: 16, color: 'inherit !important' }} />,
    email: <Mail sx={{ fontSize: 16, color: 'inherit !important' }} />,
    phone: <Phone sx={{ fontSize: 16, color: 'inherit !important' }} />,
    external: <OpenInNew sx={{ fontSize: 16, color: 'inherit !important' }} />,
    allabolag: <icons.allabolag style={{ width: 17, height: 17, color: colors.white }} />,
    fairtransport: <icons.fairtransport style={{ width: 20, height: 20, color: colors.white }} />,
    logiclink: <icons.logiclink style={{ width: 18, height: 18, color: colors.white }} />
};

const parseWebsiteLabel = (url: string) => {
    try {
        const simpleUrl = new URL(url);
        const simpleString = simpleUrl.toString();
        return simpleString.length > 28 ? `${simpleUrl.hostname}` : simpleString;
    } catch {
        return url.length > 28 ? `${url.slice(0, 28)}...` : url;
    }
};

export default function ProfileHero(props: ProfileHeroProps) {
    const {
        activeSubscription,
        allabolagUrl,
        city,
        email,
        isAccepted,
        isCarrier,
        isPending,
        isPublic,
        isRejected,
        fairtransportMembership,
        fairtransportUrl,
        logoUrl,
        name,
        orgNumber,
        phone,
        street,
        website,
        zip,
        slug,
        revenue,
        numberOfTrucks,
        recommendedByCurrentTenant = false,
        canOpenMap = false,
        onOpenMap
    } = props;

    const isFairTransport = isTenantFairtransportMember(fairtransportMembership);
    const fairTransportLevel = getFairtransportValueLevel(fairtransportMembership);
    const fairTransportTitle = fairTransportLevel !== 0
        ? `Fair Transport · Nivå ${fairTransportLevel}`
        : 'Fair Transport';

    const contacts = _.compact([
        (activeSubscription && !isPublic && isCarrier && slug) && {
            type: 'logiclink',
            label: 'Publik profilsida',
            href: formatExternalUrl(`${websiteProfileUrlBase}/${slug}`)
        },
        website && { type: 'website', label: parseWebsiteLabel(website), href: formatExternalUrl(website) },
        email && { type: 'email', label: email, href: `mailto:${email}` },
        phone && { type: 'phone', label: formatPhone(phone), href: `tel:${phone}` },
        (isCarrier && allabolagUrl) && {
            type: 'allabolag',
            label: 'allabolag.se',
            href: formatExternalUrl(`allabolag.se/${allabolagUrl}`)
        },
        (isCarrier && isFairTransport && fairtransportUrl && {
            type: 'fairtransport',
            label: 'fairtransport.se',
            href: formatExternalUrl(fairtransportUrl)
        })
    ]);

    const status = (() => {
        if (isAccepted && !isRejected) {
            return { color: colors.tealSurface, text: '✓ Verifierad profil', icon: <Verified sx={{ fontSize: 16 }} /> };
        }
        if (isPending) {
            return { color: 'rgba(255,255,255,0.7)', text: 'Verifiering pågår', icon: <HourglassTop sx={{ fontSize: 16 }} /> };
        }
        if (isRejected && !isAccepted) {
            return { color: 'rgba(255,255,255,0.55)', text: 'Ej verifierad', icon: <Cancel sx={{ fontSize: 16 }} /> };
        }
        return { color: 'rgba(255,255,255,0.55)', text: 'Ej verifierad', icon: null };
    })();

    const kpis = _.compact([
        revenue != null && { label: 'Omsättning', value: formatNumber(revenue, 'SEK') },
        numberOfTrucks != null && { label: 'Fordon', value: `${numberOfTrucks} st` }
    ]);

    const addressLine = [_.capitalize(street), zip, city].filter(Boolean).join(', ');

    return (
        <Box
            sx={{
                bgcolor: colors.blue,
                color: colors.white,
                mr: { xs: -1, sm: -2, md: -3 },
                mb: 3,
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 4, md: 6 }
            }}
        >
            {!activeSubscription && isCarrier && !isPublic && (
                <Box
                    sx={{
                        mb: 3,
                        px: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        bgcolor: colors.tealSurface,
                        color: colors.blue,
                        fontSize: 14,
                        fontWeight: 500
                    }}
                >
                    Registrera dig för att visa upp din profilsida i vår sökmotor. 3 månader gratis, därefter 849 kr/mån.
                </Box>
            )}

            <Typography sx={{ ...labelSx, color: status.color, mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {status.icon}
                {status.text}
                <Box component="span" sx={{ opacity: 0.7, letterSpacing: '0.12em' }}>
                    · {isCarrier ? carrierNameCapitalized : nonCarrierNameCapitalized}
                </Box>
            </Typography>

            {logoUrl && (
                <Box sx={{ mb: 2.5 }}>
                    <Logotype url={logoUrl} name={name} size={120} maxWidth={160} />
                </Box>
            )}

            <Typography
                component="h1"
                sx={{
                    fontFamily: FONT_FAMILY_DISPLAY,
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    fontSize: { xs: 32, sm: 44, md: 56 },
                    lineHeight: 1.05,
                    mb: 2,
                    color: colors.white
                }}
            >
                {name}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
                {city && (
                    <Button
                        variant="outlined"
                        startIcon={<Place />}
                        onClick={canOpenMap ? onOpenMap : undefined}
                        sx={{
                            height: 36,
                            px: 2,
                            color: colors.white,
                            borderColor: 'rgba(255,255,255,0.15)',
                            bgcolor: 'rgba(255,255,255,0.08)',
                            cursor: canOpenMap ? 'pointer' : 'default',
                            '&:hover': {
                                borderColor: 'rgba(255,255,255,0.35)',
                                bgcolor: 'rgba(255,255,255,0.12)'
                            }
                        }}
                    >
                        {city}
                    </Button>
                )}
                {recommendedByCurrentTenant && (
                    <Chip
                        size="small"
                        icon={<ThumbUp />}
                        label="Rekommenderat av er"
                        variant="outlined"
                        sx={onNavyChipSx}
                    />
                )}
                {isCarrier && isFairTransport && (
                    <Chip
                        size="small"
                        icon={<icons.fairtransport style={{ width: 18, height: 18, color: colors.mint }} />}
                        label={fairTransportTitle}
                        variant="outlined"
                        sx={onNavyChipSx}
                    />
                )}
            </Box>

            {kpis.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 180px))',
                        gap: 2,
                        mb: 3,
                        maxWidth: 560
                    }}
                >
                    {kpis.map((kpi) => (
                        <Box key={kpi.label}>
                            <Typography sx={{ ...labelSx, color: 'rgba(255,255,255,0.6)', mb: 0.75 }}>
                                {kpi.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_FAMILY_DISPLAY,
                                    fontWeight: 600,
                                    letterSpacing: '-0.02em',
                                    fontSize: 26,
                                    lineHeight: 1.1
                                }}
                            >
                                {kpi.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, mb: contacts.length ? 2 : 0 }}>
                {`Org.nr ${orgNumber}`}
                {addressLine ? ` · ${addressLine}` : ''}
            </Typography>

            {contacts.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {contacts.map((c) => (
                        <Chip
                            key={c.label}
                            component="a"
                            href={c.href}
                            target="_blank"
                            rel="noreferrer"
                            clickable
                            icon={ContactIcon[c.type] ?? Language}
                            label={c.label}
                            variant="outlined"
                            sx={onNavyChipSx}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}
